<template>
    <div class="charts_row">
        <article class="chart_card p-3">
            <div class="chart_canvas">
                <!-- Toggle del popover del tooltip — independiente por card
                    porque el "detalle" de cada chart tiene un costo visual
                    distinto: en el stacked el breakdown ocupa varias líneas,
                    en el activity son sólo dos métricas. El usuario decide
                    por gráfico cuál le sirve abierto. Se superpone arriba a la
                    derecha, a la altura de la leyenda del chart. -->
                <button type="button"
                    class="tooltip_toggle"
                    :class="{ 'is-active': showStackedTooltip }"
                    :aria-pressed="String(showStackedTooltip)"
                    :title="showStackedTooltip ? 'Ocultar detalle' : 'Mostrar detalle'"
                    @click="showStackedTooltip = !showStackedTooltip">
                    <span class="dot" aria-hidden="true"></span>
                    <span>Detalle</span>
                </button>
                <p v-if="error" class="chart_state chart_state--error">
                    {{ error }}
                </p>
                <p v-else-if="isLoading" class="chart_state">Cargando…</p>
                <p v-else-if="!hasStackedData" class="chart_state">
                    Sin datos de clientes para el período seleccionado.
                </p>
                <SalesClientsStackedChart v-else
                    :month-headers="chartsData.monthHeaders"
                    :series="chartsData.byClient"
                    :show-tooltip="showStackedTooltip" />
            </div>
        </article>

        <article class="chart_card p-3">
            <div class="chart_canvas">
                <button type="button"
                    class="tooltip_toggle"
                    :class="{ 'is-active': showLineTooltip }"
                    :aria-pressed="String(showLineTooltip)"
                    :title="showLineTooltip ? 'Ocultar detalle' : 'Mostrar detalle'"
                    @click="showLineTooltip = !showLineTooltip">
                    <span class="dot" aria-hidden="true"></span>
                    <span>Detalle</span>
                </button>
                <p v-if="error" class="chart_state chart_state--error">
                    {{ error }}
                </p>
                <p v-else-if="isLoading" class="chart_state">Cargando…</p>
                <p v-else-if="!hasStackedData" class="chart_state">
                    Sin datos de clientes para el período seleccionado.
                </p>
                <SalesClientsLineChart v-else
                    :month-headers="chartsData.monthHeaders"
                    :series="chartsData.byClient"
                    :show-tooltip="showLineTooltip" />
            </div>
        </article>
    </div>
</template>

<script>
import SalesClientsStackedChart from "@/components/financialReports/charts/SalesClientsStackedChart.vue";
import SalesClientsLineChart from "@/components/financialReports/charts/SalesClientsLineChart.vue";

// Row de los dos gráficos del header del tab Ventas. Maneja loading/error/
// empty states y delega el rendering a los dos componentes de chart, cada
// uno sobre su propia card.
//
// Decisión: el container conoce el shape de `chartsData` (lo provee el store)
// y reparte slices a cada chart. Los charts no importan Vuex — reciben arrays
// por prop. Mismo patrón que BodySalesAnalysis con sus cards.

export default {
    name: "SalesChartsRow",

    components: { SalesClientsStackedChart, SalesClientsLineChart },

    props: {
        // El payload normalizado del store (`salesReport.chartsData`):
        //   { monthHeaders, byClient[], clientCounts[], avgTickets[],
        //     hasOtros, topN, periodRange }.
        // `null` antes del primer fetch — el row renderea loading/empty.
        chartsData: { type: Object, default: null },
        isLoading: { type: Boolean, default: false },
        error: { type: String, default: "" },
    },

    data() {
        return {
            // Estado local de cada toggle de tooltip. No vive en el store: es
            // preferencia visual del row, no estado del dominio. Ambos charts
            // (stacked + líneas) consumen la misma serie por cliente.
            showStackedTooltip: true,
            showLineTooltip: true,
        };
    },

    computed: {
        // Ambos charts del row usan `byClient` — un solo guard de datos sirve
        // para los dos.
        hasStackedData() {
            return Boolean(
                this.chartsData
                && this.chartsData.monthHeaders.length > 0
                && this.chartsData.byClient.length > 0,
            );
        },
    },
};
</script>

<style scoped lang="scss">
.charts_row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    width: 100%;
}

// Misma surface treatment que StoresDistributionCard — surface blanca + border
// suave + radius 16 + whisper shadow. Los charts adentro se vuelven transparent
// para reusar este chrome.
.chart_card {
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 16px;
    box-shadow: var(--report-shadow-card);
    height: 340px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

// Toggle "Detalle": pill compacto, mismo léxico visual que .filter_btn del
// TabHeader (border-radius 999 sería overkill — usamos 8 para integrarlo con
// la chrome de cards). Dot a la izquierda actúa como indicador de estado:
// rellena cuando is-active (tooltips ON), outline cuando off.
// Se superpone arriba a la derecha del canvas, centrado sobre la banda de la
// leyenda del chart (legend.top:4, alto ~16px → centro ~12px). top:0 deja el
// centro del pill (~13px) alineado con el de la leyenda.
.tooltip_toggle {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: transparent;
    border: 1px solid var(--report-border);
    border-radius: 8px;
    color: var(--report-text-muted);
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease, border-color 160ms ease;

    .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        border: 1px solid currentColor;
        background: transparent;
        transition: background-color 160ms ease;
    }

    &:hover {
        background: var(--report-surface);
        color: var(--report-text);
    }

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 2px;
    }

    &.is-active {
        background: var(--report-accent-bg);
        border-color: var(--report-accent-border);
        color: var(--report-accent-text);

        .dot {
            background: currentColor;
        }
    }
}

.chart_canvas {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    position: relative;
}

.chart_state {
    margin: auto;
    color: var(--report-text-muted);
    font-size: 13px;
    text-align: center;

    &--error {
        color: var(--report-negative);
    }
}

// Sotck vertical en viewport angosto — los charts apretados a <500px de ancho
// pierden legibilidad. 1024px es el breakpoint usual del módulo (mismo donde
// el sidebar del dashboard colapsa).
@media (max-width: 1024px) {
    .charts_row {
        grid-template-columns: 1fr;
    }
}
</style>
